import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import { TODAY } from '@/constants/dateTime'
import {
  GET_ACTIVATION_GROUP_RESOURCES_API,
  getActivationGroupResources,
} from '@/features/activationGroup/endpoints/activationGroups'
import type { ActivationGroup } from '@/features/activationGroup/types'
import { ResourceCategory } from '@/features/activationGroup/types'
import { useForecastQuery } from '@/features/bessDashboard/hooks/usePriceForecastQuery'
import type { FormattedRow } from '@/features/bessDashboard/types'
import { formatTimestampToUtcIso } from '@/features/bessDashboard/utils/utils'
import type { MarketProgram } from '@/features/bidding/constants'
import { BidType, CASCADE_MARKETS } from '@/features/bidding/constants'
import { useAggregateAvailabilityQuery } from '@/features/bidding/hooks/useAggregateAvailabilityQuery'
import { useAggregateSuggestedBidsQuery } from '@/features/bidding/hooks/useAggregateSuggestedBidsQuery'
import { useBidOfferEligibleActivationGroups } from '@/features/bidding/hooks/useBidOfferEligibleActivationGroups'
import { useCompletedBidMarketsQuery } from '@/features/bidding/hooks/useCompletedBidMarketsQuery'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateMarket, AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevel } from '@/features/bidding/types/bidLadder'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { hasActivationGroupsConcept } from '@/features/bidding/utils/marketCapabilities'

import { useBidOfferCascadingBidLevels } from './useBidOfferCascadingBidLevels'
import type { RegulationObjectGroup } from './useBidOfferCascadingDerivedData'
import { useBidOfferCascadingDerivedData } from './useBidOfferCascadingDerivedData'

const PORTFOLIO_CODE = 'FI-A'
const BESS_RESOURCE_CATEGORY = 'BESS'

export type BidOfferCascadingData = {
  regulationObjectGroups: RegulationObjectGroup[]
  capacityByActivationGroup: Map<string, number>
  activationGroupsMap: Map<string, ActivationGroup>
  bidLevels: BidLevel[]
  forecastsData: FormattedRow[] | undefined
  bidStatus: Record<CascadeMarketId, boolean>
  aggregateSuggestedBids: AggregateSuggestedBidResponse | null
  aggregateAvailability: AggregateAvailabilityResponse | null
  handleAddBidLevel: (volume: number, upPrice: number, downPrice: number) => void
  handleRemoveBidLevel: (index: number) => void
}

type UseBidOfferCascadingDataReturn =
  | { isLoading: true; data: null }
  | { isLoading: false; data: BidOfferCascadingData }

type UseBidOfferCascadingDataParams = {
  deliveryDay: MarketDate
  selectedMarket: CascadeMarketId
}

const AGGREGATE_MARKET_MAP: Record<CascadeMarketId, AggregateMarket | null> = {
  mfrr: null,
  'da-wholesale': null,
  'fcr-d': 'FCRD',
  'fcr-n': 'FCRN',
}

export function useBidOfferCascadingData({
  deliveryDay,
  selectedMarket,
}: UseBidOfferCascadingDataParams): UseBidOfferCascadingDataReturn {
  // Get the market configuration for the selected cascade market
  const market = CASCADE_MARKETS.find((m) => m.id === selectedMarket)!

  const marketPrograms = market.marketPrograms
  // Symmetrical markets (like FCR-N) have a single program that handles both directions
  // Asymmetrical markets (like mFRR, FCR-D) have separate UP and DOWN programs
  const isSymmetricalMarket = marketPrograms.length === 1

  const upOrSymmetricalProgram = marketPrograms[0] as MarketProgram
  const downProgram = isSymmetricalMarket ? upOrSymmetricalProgram : (marketPrograms[1] as MarketProgram)

  // Check if this market uses the activation groups concept (only mFRR does)
  const marketHasActivationGroups = hasActivationGroupsConcept(selectedMarket)

  // 1. Fetch eligible activation groups for UP direction (or symmetrical market)
  // Only fetch if this market uses activation groups
  const { eligibleActivationGroups: upOrSymmetricalGroups, isLoading: isLoadingUpOrSymmetrical } =
    useBidOfferEligibleActivationGroups(
      {
        marketProgram: upOrSymmetricalProgram,
        deliveryDay,
        portfolioCode: 'FI-A',
        bidType: BidType.CAPACITY,
      },
      { enabled: marketHasActivationGroups },
    )

  // 2. Fetch eligible activation groups for DOWN direction (only for asymmetrical markets with activation groups)
  const { eligibleActivationGroups: downGroups, isLoading: isLoadingDown } = useBidOfferEligibleActivationGroups(
    {
      marketProgram: downProgram,
      deliveryDay,
      portfolioCode: 'FI-A',
      bidType: BidType.CAPACITY,
    },
    { enabled: marketHasActivationGroups && !isSymmetricalMarket },
  )

  // DOWN direction is ready when: it's a symmetrical market (no DOWN) OR DOWN has finished loading
  const isDownDirectionReady = isSymmetricalMarket || !isLoadingDown

  // 3. Combine activation groups and filter by BESS
  // For symmetrical markets, only use upOrSymmetricalGroups
  // This must be done before the useQueries call since hooks cannot be called conditionally
  const eligibleActivationGroups = useMemo(() => {
    const groups = isSymmetricalMarket
      ? (upOrSymmetricalGroups ?? [])
      : [...(upOrSymmetricalGroups ?? []), ...(downGroups ?? [])]
    return groups.filter((group) => group.resourceCategory === ResourceCategory.BESS)
  }, [isSymmetricalMarket, upOrSymmetricalGroups, downGroups])

  // 4. Fetch resources for each BESS activation group (only for markets with activation groups)
  const resourcesQueryResults = useQueries({
    queries: eligibleActivationGroups.map((activationGroup) => ({
      queryKey: [GET_ACTIVATION_GROUP_RESOURCES_API, { activationGroupUuid: activationGroup.uuid }],
      queryFn: () => getActivationGroupResources(activationGroup.uuid),
      enabled: marketHasActivationGroups && !isLoadingUpOrSymmetrical && isDownDirectionReady,
    })),
  })

  useErrorHandler(resourcesQueryResults.some((result) => result.isError))

  const { capacityByActivationGroup, regulationObjectGroups } = useBidOfferCascadingDerivedData({
    eligibleActivationGroups,
    resourcesQueryResults,
  })

  // Create a map of activation groups by UUID for quick lookup
  const activationGroupsMap = useMemo(() => {
    const map = new Map<string, ActivationGroup>()
    eligibleActivationGroups.forEach((ag) => map.set(ag.uuid, ag))
    return map
  }, [eligibleActivationGroups])

  const isLoadingResources = resourcesQueryResults.some((result) => result.isLoading)

  // 5. Get bid levels (mocked data for now, will come from backend)
  const { bidLevels, handleAddBidLevel, handleRemoveBidLevel } = useBidOfferCascadingBidLevels()

  // 6. Fetch forecast data
  const startTime = TODAY.minus({ days: 7 })
  const endTime = TODAY.plus({ days: 5 })

  const { data: forecastsData, isLoading: isLoadingForecasts } = useForecastQuery(
    formatTimestampToUtcIso(startTime),
    formatTimestampToUtcIso(endTime.plus({ hours: 23 })),
  )

  // 7. Fetch completed bid markets to determine which markets have bids submitted
  const { bidStatus, isLoading: isLoadingBidStatus } = useCompletedBidMarketsQuery({
    deliveryDay,
    portfolioCode: PORTFOLIO_CODE,
    resourceCategory: BESS_RESOURCE_CATEGORY,
  })

  // 8. Fetch aggregate suggested bids (only for FCR markets)
  const aggregateMarket = AGGREGATE_MARKET_MAP[selectedMarket]
  const { aggregateSuggestedBids, isLoading: isLoadingAggregateSuggestedBids } = useAggregateSuggestedBidsQuery(
    {
      deliveryDay,
      portfolioCode: PORTFOLIO_CODE,
      market: aggregateMarket,
      resourceCategory: BESS_RESOURCE_CATEGORY,
    },
    { useMockData: false },
  )

  // 9. Fetch aggregate availability (only for FCR markets)
  const { aggregateAvailability, isLoading: isLoadingAggregateAvailability } = useAggregateAvailabilityQuery(
    {
      deliveryDay,
      portfolioCode: PORTFOLIO_CODE,
      market: aggregateMarket,
      resourceCategory: BESS_RESOURCE_CATEGORY,
    },
    { useMockData: false },
  )

  // Check if still loading
  // For markets without activation groups, skip waiting for activation group data
  const isLoadingActivationGroups = marketHasActivationGroups
    ? isLoadingUpOrSymmetrical || !isDownDirectionReady || isLoadingResources
    : false
  const isLoading =
    isLoadingActivationGroups ||
    isLoadingForecasts ||
    isLoadingBidStatus ||
    isLoadingAggregateSuggestedBids ||
    isLoadingAggregateAvailability

  if (isLoading) {
    return { isLoading: true, data: null }
  }

  return {
    isLoading: false,
    data: {
      regulationObjectGroups,
      capacityByActivationGroup,
      activationGroupsMap,
      bidLevels,
      forecastsData,
      bidStatus,
      aggregateSuggestedBids,
      aggregateAvailability,
      handleAddBidLevel,
      handleRemoveBidLevel,
    },
  }
}
